/**
 * Spec 10.2.1 §2.1 — one approvals-inbox row = one pending authorisation subject.
 *
 * Collapsed: subject type + amount, requesting actor (agent/human avatar), live
 * expiry countdown, mode label. Expanded (click): embeds the shared SummaryCard
 * UNCHANGED, then the approve/decline bar. Overdraw rows are visibly distinct.
 * Approving is content-bound — the label references THIS subject and the
 * bind-note states that any edit re-hashes and voids the request. Expanding
 * mutates nothing.
 */
import { useState } from "react";
import { SummaryCard } from "./SummaryCard";
import { Countdown } from "./Countdown";
import { requestedByLabel } from "./format";
import { useApproveAuthorisation, useDeclineAuthorisation, type PendingAuthorisation } from "../../hooks/use-ledger-approvals";

/** A minimal per-subject glyph (kept markup-light; one vocabulary). */
function SubjectIcon({ kind }: { kind: string }) {
  const p: Record<string, string> = {
    transaction: "M4 7h16v10H4zM4 11h16",
    period_close: "M4 5h16v16H4zM4 9h16M8 3v4M16 3v4",
    reconciliation_finalise: "M7 7h10M7 12h10M7 17h6M4 4v16",
    account_close: "M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16M9 21v-4h6v4",
    deposit_banking: "M3 10 12 4l9 6M5 10v9h14v-9",
    instrument_cancellation: "M4 7h16v10H4zM4 11h16M6 15l12-8",
    hold_release: "M6 10V7a6 6 0 0 1 12 0v3M5 10h14v10H5z",
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
      <path d={p[kind] ?? p.transaction} />
    </svg>
  );
}

export function AuthRow({ auth, defaultOpen = false }: { auth: PendingAuthorisation; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const approve = useApproveAuthorisation();
  const decline = useDeclineAuthorisation();
  const busy = approve.isPending || decline.isPending;

  const s = auth.summary;
  const cls = ["auth-row", open ? "is-open" : "", s.overdraw ? "is-overdraw" : ""].filter(Boolean).join(" ");

  return (
    <div className={cls} onClick={() => setOpen((v) => !v)}>
      <span className="auth-row__icon" title={auth.subject_kind.replace(/_/g, " ")}><SubjectIcon kind={auth.subject_kind} /></span>
      <div className="auth-row__main">
        <div className="auth-row__subject">
          <span className="title">{s.title}</span>
          {s.overdraw ? <span className="pill pill--pending"><span className="dot" />overdraw path</span> : null}
        </div>
        <div className="auth-row__gloss">{s.gloss}</div>
        <div className="auth-row__actor">
          <span className={`avatar av-xs${auth.requested_by.kind === "agent" ? " avatar--agent" : ""}`}>
            {(auth.requested_by.label ?? auth.requested_by.kind).slice(0, 1).toUpperCase()}
          </span>
          prepared by {requestedByLabel(auth.requested_by)} · {auth.ledger_name}
        </div>
      </div>
      <div className="auth-row__side">
        <Countdown expiresAt={auth.expires_at} />
        <span className="muted tiny">{auth.mode === "out_of_band" ? "out-of-band" : auth.mode}</span>
      </div>

      {open && (
        <div className="auth-detail" onClick={(e) => e.stopPropagation()}>
          <SummaryCard summary={s} contentHash={auth.content_hash} expiresAt={auth.expires_at} requestedBy={auth.requested_by} />

          {approve.isError || decline.isError ? (
            <div className="stale-banner">
              <svg className="icon" viewBox="0 0 24 24"><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>
              <div>{(approve.error as Error)?.message ?? (decline.error as Error)?.message}</div>
            </div>
          ) : null}

          {declining ? (
            <div className="approve-bar">
              <input
                className="split-line-input"
                style={{ flex: 1, fontFamily: "var(--font-sans)", fontSize: 13, padding: "7px 10px", border: "0.5px solid var(--color-border-secondary)", borderRadius: "var(--border-radius-md)", background: "var(--color-background-primary)", color: "var(--color-text-primary)" }}
                placeholder="Reason for declining (required)"
                value={reason}
                autoFocus
                onChange={(e) => setReason(e.target.value)}
              />
              <button onClick={() => { setDeclining(false); setReason(""); }} disabled={busy}>Cancel</button>
              <button
                className="btn--decline"
                disabled={busy || reason.trim().length === 0}
                onClick={() => decline.mutate({ authorisationId: auth.id, reason: reason.trim() }, { onSuccess: () => { setDeclining(false); setReason(""); } })}
              >Confirm decline</button>
            </div>
          ) : (
            <div className="approve-bar">
              <span className="bind-note">Approving authorises precisely the content above. Any edit re-hashes the draft and voids this request.</span>
              <button className="btn--decline" disabled={busy} onClick={() => setDeclining(true)}>Decline…</button>
              <button className="btn--approve" data-variant="primary" disabled={busy} onClick={() => approve.mutate({ authorisation_id: auth.id, subject_kind: auth.subject_kind, subject_id: auth.subject_id, overdraw: auth.summary.overdraw })}>
                {approve.isPending ? "Approving…" : `Approve this ${auth.subject_kind === "transaction" ? (s.txn_summary?.type ?? "post") : auth.subject_kind.replace(/_/g, " ")}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
